import { useEffect, useState, type ImgHTMLAttributes } from 'react';
import { isVideoFile } from '../../shared/fileUtils';
import { catalogImageSrcSet, catalogImageThumbnail } from '../../shared/catalogLookbook';
import { Skeleton } from '../Skeleton/Skeleton';

export const CATALOG_MEDIA_SIZES = {
  grid: '(max-width: 767px) 50vw, (max-width: 1023px) 33vw, 25vw',
  hero: '100vw',
  detail: '(max-width: 1023px) 100vw, 50vw',
  thumb: '64px',
} as const;

type PublicCatalogMediaProps = {
  src: string;
  alt?: string;
  className?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  fetchPriority?: 'high' | 'low' | 'auto';
  variant?: 'responsive' | 'thumbnail';
  controls?: boolean;
};

const PublicCatalogMedia = ({
  src,
  alt = '',
  className = '',
  sizes,
  loading = 'lazy',
  fetchPriority,
  variant = 'responsive',
  controls = false,
}: PublicCatalogMediaProps) => {
  const isVideo = isVideoFile(src);
  const [loaded, setLoaded] = useState(false);
  const [useSrcSet, setUseSrcSet] = useState(true);
  const [failedThumbnail, setFailedThumbnail] = useState(false);

  useEffect(() => {
    setLoaded(false);
    setUseSrcSet(true);
    setFailedThumbnail(false);
  }, [src]);

  const srcSet = variant === 'responsive' && useSrcSet ? catalogImageSrcSet(src) : '';
  const imageSrc = variant === 'thumbnail' && !failedThumbnail ? catalogImageThumbnail(src) : src;

  const markLoaded = () => setLoaded(true);

  const handleImageError = () => {
    if (variant === 'thumbnail' && !failedThumbnail && imageSrc !== src) {
      setFailedThumbnail(true);
      return;
    }
    if (useSrcSet && srcSet) {
      setUseSrcSet(false);
    }
  };

  return (
    <div className="relative h-full w-full">
      {!loaded && (
        <Skeleton className="absolute inset-0 h-full w-full rounded-none bg-as-pink-100" />
      )}
      {isVideo ? (
        <video
          className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'}`}
          src={src}
          muted
          playsInline
          autoPlay={variant !== 'thumbnail'}
          loop={variant !== 'thumbnail'}
          controls={controls}
          preload="metadata"
          onCanPlay={markLoaded}
          onLoadedData={markLoaded}
        />
      ) : (
        <img
          src={imageSrc}
          srcSet={srcSet || undefined}
          sizes={sizes}
          alt={alt}
          loading={loading}
          {...(fetchPriority ? ({ fetchpriority: fetchPriority } as ImgHTMLAttributes<HTMLImageElement>) : {})}
          className={`${className} ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onLoad={markLoaded}
          onError={handleImageError}
        />
      )}
    </div>
  );
};

export default PublicCatalogMedia;
